import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import api, { formatErr } from "@/lib/api";
import { useDialog } from "@/context/DialogProvider";
import { useSite } from "@/context/SiteContext";
import { useAuth } from "@/context/AuthContext";
import { Plus, Trash2, Pencil, Building2, MapPin, ClipboardCheck, Sparkles } from "lucide-react";
import SiteQRPanel from "@/components/SiteQRPanel";
import SiteCalendarPanel from "@/components/SiteCalendarPanel";

const TAB_THRESHOLD = 5;

const FACILITY_CATEGORIES = [
  { value: "food_production", label: "Food Production Area", short: "Food Production" },
  { value: "storage",         label: "Storage",              short: "Storage" },
  { value: "restroom",        label: "Restroom",             short: "Restroom" },
  { value: "receiving",       label: "Receiving / Loading",  short: "Receiving" },
  { value: "office",          label: "Office",               short: "Office" },
  { value: "other",           label: "Other",                short: "Other" },
];
const CATEGORY_LABEL = Object.fromEntries(FACILITY_CATEGORIES.map((c) => [c.value, c.short]));

const CLEANLINESS_SUBAREAS = [
  { value: "production", label: "Production Area" },
  { value: "restroom",   label: "Restroom" },
  { value: "storage",    label: "Storage Areas" },
];

export default function Sites() {
  const { confirm, notify } = useDialog();
  const { activeSiteId: headerSiteId, isAllSites, loaded: sitesLoaded } = useSite();
  const { user } = useAuth();
  const canEditCalendar = user?.role === "owner" || user?.role === "manager";
  const [sites, setSites] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [team, setTeam] = useState([]);
  // Page-local tab selection only matters when the header is set to "All sites";
  // otherwise the active site is locked to whatever the header dictates.
  const [localTabId, setLocalTabId] = useState(null);
  const [modal, setModal] = useState(null); // "site" | "facility" | null
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({});
  const [err, setErr] = useState("");

  // Effective active site: header wins when it points at a specific site,
  // otherwise fall back to the local tab.
  const activeSiteId = isAllSites ? localTabId : headerSiteId;

  const load = async () => {
    const [s, f, t] = await Promise.all([
      api.get("/sites"),
      api.get("/facilities"),
      api.get("/tenant/directory"),
    ]);
    const sl = s.data.sites || [];
    setSites(sl);
    setFacilities(f.data.facilities || []);
    setTeam(t.data.users || []);
    // Seed / reconcile the local tab when in All-sites mode.
    if (isAllSites) {
      if (!localTabId && sl.length) setLocalTabId(sl[0].id);
      else if (localTabId && !sl.find((x) => x.id === localTabId) && sl.length) setLocalTabId(sl[0].id);
    }
  };
  useEffect(() => {
    if (!sitesLoaded) return;
    load();
    // Re-load when the header site selection changes so /facilities (site-scoped) refreshes.
    // eslint-disable-next-line
  }, [sitesLoaded, headerSiteId, isAllSites]);

  const activeSite = useMemo(() => sites.find((s) => s.id === activeSiteId), [sites, activeSiteId]);
  const siteFacilities = useMemo(
    () => facilities.filter((f) => f.site_id === activeSiteId),
    [facilities, activeSiteId]
  );

  // Team members assignable as Person-in-Charge for the facility's site:
  // - Users with empty site_ids (owner / chain manager) see everywhere → included.
  // - Users explicitly assigned to the facility's site.
  const picOptions = useMemo(() => {
    const siteId = form?.site_id;
    if (!siteId) return [];
    return team
      .filter((u) => {
        const siteIds = u.site_ids || [];
        return siteIds.length === 0 || siteIds.includes(siteId);
      })
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [team, form?.site_id]);

  // ---------- Site modal ----------
  const openAddSite = () => {
    setEditingId(null);
    setForm({ name: "", location: "" });
    setModal("site"); setErr("");
  };
  const openEditSite = (s) => {
    setEditingId(s.id);
    setForm({ name: s.name, location: s.location || "" });
    setModal("site"); setErr("");
  };

  // ---------- Facility modal ----------
  const openAddFacility = () => {
    if (!activeSiteId) return;
    setEditingId(null);
    setForm({
      site_id: activeSiteId,
      name: "", location: "", person_in_charge: "",
      category: "food_production",
      daily_hygiene_enabled: false,
      daily_cleanliness_enabled: false,
      cleanliness_areas: ["production", "restroom", "storage"],
    });
    setModal("facility"); setErr("");
  };
  const openEditFacility = (f) => {
    setEditingId(f.id);
    setForm({
      site_id: f.site_id, name: f.name,
      location: f.location || "", person_in_charge: f.person_in_charge || "",
      category: f.category || "food_production",
      daily_hygiene_enabled: !!f.daily_hygiene_enabled,
      daily_cleanliness_enabled: !!f.daily_cleanliness_enabled,
      cleanliness_areas: Array.isArray(f.cleanliness_areas) && f.cleanliness_areas.length
        ? f.cleanliness_areas : ["production", "restroom", "storage"],
    });
    setModal("facility"); setErr("");
  };

  const toggleCleanlinessArea = (code) => {
    setForm((f) => {
      const has = (f.cleanliness_areas || []).includes(code);
      const next = has
        ? (f.cleanliness_areas || []).filter((a) => a !== code)
        : [...(f.cleanliness_areas || []), code];
      return { ...f, cleanliness_areas: next };
    });
  };

  const submit = async (e) => {
    e.preventDefault(); setErr("");
    try {
      if (modal === "site") {
        const payload = {
          name: form.name,
          location: form.location || "",
          timezone: "UTC",
        };
        if (editingId) await api.put(`/sites/${editingId}`, payload);
        else {
          const { data } = await api.post("/sites", payload);
          if (data?.site?.id && isAllSites) setLocalTabId(data.site.id);
        }
      } else if (modal === "facility") {
        const payload = {
          site_id: form.site_id,
          name: form.name,
          location: form.location || "",
          person_in_charge: form.person_in_charge || "",
          category: form.category || "food_production",
          daily_hygiene_enabled: !!form.daily_hygiene_enabled,
          daily_cleanliness_enabled: !!form.daily_cleanliness_enabled,
          cleanliness_areas: form.daily_cleanliness_enabled
            ? (form.cleanliness_areas || []).filter((a) => ["production", "restroom", "storage"].includes(a))
            : ["production", "restroom", "storage"],
        };
        if (editingId) await api.put(`/facilities/${editingId}`, payload);
        else await api.post("/facilities", payload);
      }
      setModal(null); setEditingId(null); load();
    } catch (e) { setErr(formatErr(e.response?.data?.detail)); }
  };

  const delSite = async (s) => {
    if (!await confirm({ title: "Delete site?", message: `Remove "${s.name}". This cannot be undone.`, danger: true, confirmLabel: "Delete" })) return;
    try { await api.delete(`/sites/${s.id}`); load(); }
    catch (e) { await notify({ title: "Couldn't delete", message: formatErr(e.response?.data?.detail), variant: "err" }); }
  };
  const delFacility = async (f) => {
    if (!await confirm({ title: "Delete facility?", message: `Remove "${f.name}". This cannot be undone.`, danger: true, confirmLabel: "Delete" })) return;
    try { await api.delete(`/facilities/${f.id}`); load(); }
    catch (e) { await notify({ title: "Couldn't delete", message: formatErr(e.response?.data?.detail), variant: "err" }); }
  };

  const useDropdown = sites.length > TAB_THRESHOLD;

  return (
    <div className="vb-fade-in" data-testid="sites-page">
      <div className="mb-6">
        <h1 className="vb-heading text-3xl mb-1" style={{ color: "var(--vb-text-2)" }}>Sites</h1>
        <p className="opacity-70">Each site is an outlet / property. Within a site you can add facilities (kitchens, bars, etc.) where equipment lives.</p>
      </div>

      {/* Site selector + add site
           - When header = "All sites": show tabs (or dropdown if > threshold) to switch within page
           - When header = specific site: hide tabs entirely; page is locked to that site.
      */}
      <div className="flex justify-between items-center mb-4 flex-wrap gap-3" data-testid="sites-toolbar">
        {sites.length === 0 ? (
          <span className="opacity-70">No sites yet — add your first site to get started.</span>
        ) : !isAllSites ? (
          <div className="vb-pill text-xs flex items-center gap-2" data-testid="sites-header-lock-hint">
            <Building2 size={12} />
            Viewing <strong>{sites.find((s) => s.id === headerSiteId)?.name || "selected site"}</strong> — switch sites from the header.
          </div>
        ) : useDropdown ? (
          <select
            className="vb-input" style={{ maxWidth: 340 }}
            value={activeSiteId || ""} onChange={(e) => setLocalTabId(e.target.value)}
            data-testid="site-selector"
          >
            {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        ) : (
          <div className="vb-tabs" role="tablist" style={{ flexWrap: "wrap" }}>
            {sites.map((s) => (
              <button
                key={s.id} role="tab"
                aria-selected={s.id === activeSiteId}
                className={`vb-tab ${s.id === activeSiteId ? "active" : ""}`}
                onClick={() => setLocalTabId(s.id)}
                data-testid={`site-tab-${s.id}`}
              >
                {s.name}
              </button>
            ))}
          </div>
        )}
        <button className="vb-btn text-sm" onClick={openAddSite} data-testid="add-site"><Plus size={16} /> Add site</button>
      </div>

      {/* Active site — metadata + facilities */}
      {activeSite && (
        <>
          <div className="vb-card p-5 mb-4 flex justify-between items-start flex-wrap gap-3" data-testid="site-summary">
            <div className="flex-1 min-w-[240px]">
              <div className="vb-heading text-xl mb-1" style={{ color: "var(--vb-text-2)" }}>{activeSite.name}</div>
              <div className="opacity-70 text-sm flex items-start gap-1.5">
                <MapPin size={13} className="mt-0.5 flex-shrink-0 opacity-60" />
                {activeSite.location || <em className="opacity-50">No address yet</em>}
              </div>
            </div>
            <div className="flex gap-2">
              <button className="vb-btn ghost text-sm" onClick={() => openEditSite(activeSite)} data-testid="edit-site"><Pencil size={14} /> Edit</button>
              <button className="vb-btn ghost text-sm" onClick={() => delSite(activeSite)} data-testid="del-site"><Trash2 size={14} /> Delete</button>
            </div>
          </div>

          <div className="flex justify-between items-center mb-3">
            <h2 className="vb-heading text-lg" style={{ color: "var(--vb-text-2)" }}>
              <Building2 size={18} className="inline mr-2 -mt-1" />Facilities
            </h2>
            <button className="vb-btn text-sm" onClick={openAddFacility} data-testid="add-facility"><Plus size={16} /> Add facility</button>
          </div>

          <div className="vb-card overflow-x-auto">
            <table className="vb-table">
              <thead><tr><th>Name</th><th>Category</th><th>Modules</th><th>Location</th><th>Person in charge</th><th></th></tr></thead>
              <tbody>
                {siteFacilities.length === 0 && (
                  <tr><td colSpan={6} style={{ textAlign: "center", padding: 28 }} className="opacity-60">
                    No facilities under this site yet. Add a kitchen, bar, or any sub-unit.
                  </td></tr>
                )}
                {siteFacilities.map((f) => {
                  const cat = f.category || "food_production";
                  return (
                  <tr key={f.id} data-testid={`facility-row-${f.id}`}>
                    <td><strong>{f.name}</strong></td>
                    <td>
                      <span
                        className="vb-pill text-[11px]"
                        data-testid={`facility-category-${f.id}`}
                      >
                        {CATEGORY_LABEL[cat] || cat}
                      </span>
                    </td>
                    <td>
                      <div className="flex flex-wrap gap-1">
                        {f.daily_hygiene_enabled && (
                          <span className="vb-pill ok text-[10px]" title="Daily Hygiene applies"
                            data-testid={`facility-mod-hyg-${f.id}`}>
                            <ClipboardCheck size={10} /> Hygiene
                          </span>
                        )}
                        {f.daily_cleanliness_enabled && (
                          <span className="vb-pill ok text-[10px]"
                            title={`Cleanliness areas: ${(f.cleanliness_areas || []).join(", ") || "—"}`}
                            data-testid={`facility-mod-clean-${f.id}`}>
                            <Sparkles size={10} /> Cleanliness · {(f.cleanliness_areas || []).length}/{CLEANLINESS_SUBAREAS.length}
                          </span>
                        )}
                        {!f.daily_hygiene_enabled && !f.daily_cleanliness_enabled && (
                          <span className="text-[11px] opacity-50">—</span>
                        )}
                      </div>
                    </td>
                    <td>{f.location || "—"}</td>
                    <td>{f.person_in_charge || "—"}</td>
                    <td>
                      <div className="flex gap-1 justify-end">
                        <button className="vb-btn ghost text-sm" onClick={() => openEditFacility(f)} data-testid={`edit-facility-${f.id}`} title="Edit"><Pencil size={14} /></button>
                        <button className="vb-btn ghost text-sm" onClick={() => delFacility(f)} data-testid={`del-facility-${f.id}`} title="Delete"><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <SiteQRPanel site={activeSite} facilities={siteFacilities} />
          <SiteCalendarPanel site={activeSite} canEdit={canEditCalendar} />
        </>
      )}

      {modal && createPortal(
        <div className="vb-modal-bg" onClick={(e) => { if (e.target === e.currentTarget) setModal(null); }}>
          <div className="vb-modal">
            <h2>
              {modal === "site"
                ? (editingId ? "Edit site" : "Add site")
                : (editingId ? "Edit facility" : "Add facility")}
            </h2>
            {err && <div className="vb-pill err mb-3">{err}</div>}
            <form onSubmit={submit} className="grid gap-3">
              {modal === "site" ? (
                <>
                  <input className="vb-input" required placeholder="Name (e.g. Grand Hyatt Singapore)" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="site-name" />
                  <input className="vb-input" placeholder="Address" value={form.location || ""} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="site-address" />
                </>
              ) : (
                <>
                  <input className="vb-input" required placeholder="Name (e.g. Lime Cafe Kitchen)" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="facility-name" />
                  <div>
                    <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider opacity-70">Category</label>
                    <select
                      className="vb-input"
                      value={form.category || "food_production"}
                      onChange={(e) => setForm({ ...form, category: e.target.value })}
                      data-testid="facility-category-select"
                    >
                      {FACILITY_CATEGORIES.map((c) => (
                        <option key={c.value} value={c.value}>{c.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider opacity-70">Compliance modules</label>
                    <p className="text-[11px] opacity-60 mb-2">
                      Select which daily checks apply to this facility. Disable both if no daily checklist is required here.
                    </p>
                    <label className="flex items-start gap-2 px-3 py-2 rounded cursor-pointer text-sm mb-2"
                      style={{
                        border: `1px solid ${form.daily_hygiene_enabled ? "var(--vb-accent)" : "rgba(0,0,0,0.10)"}`,
                        background: form.daily_hygiene_enabled ? "color-mix(in srgb, var(--vb-accent) 10%, transparent)" : "transparent",
                      }}
                      data-testid="facility-mod-hygiene-row"
                    >
                      <input
                        type="checkbox" className="mt-0.5"
                        checked={!!form.daily_hygiene_enabled}
                        onChange={(e) => setForm({ ...form, daily_hygiene_enabled: e.target.checked })}
                        data-testid="facility-mod-hygiene-input"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-1.5"><ClipboardCheck size={13} /> <strong>Daily Hygiene</strong></div>
                        <div className="text-[11px] opacity-60">Quick daily hand-wash / sanitation observation (VB-DH).</div>
                      </div>
                    </label>
                    <label className="flex items-start gap-2 px-3 py-2 rounded cursor-pointer text-sm"
                      style={{
                        border: `1px solid ${form.daily_cleanliness_enabled ? "var(--vb-accent)" : "rgba(0,0,0,0.10)"}`,
                        background: form.daily_cleanliness_enabled ? "color-mix(in srgb, var(--vb-accent) 10%, transparent)" : "transparent",
                      }}
                      data-testid="facility-mod-cleanliness-row"
                    >
                      <input
                        type="checkbox" className="mt-0.5"
                        checked={!!form.daily_cleanliness_enabled}
                        onChange={(e) => setForm({ ...form, daily_cleanliness_enabled: e.target.checked })}
                        data-testid="facility-mod-cleanliness-input"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-1.5"><Sparkles size={13} /> <strong>Daily Cleanliness</strong></div>
                        <div className="text-[11px] opacity-60">Per-area audit (VB-PRP-07). Tick the areas this facility actually has below.</div>
                        {form.daily_cleanliness_enabled && (
                          <div className="mt-2 flex flex-wrap gap-1.5" onClick={(e) => e.preventDefault()} data-testid="facility-clean-areas">
                            {CLEANLINESS_SUBAREAS.map((a) => {
                              const checked = (form.cleanliness_areas || []).includes(a.value);
                              return (
                                <label key={a.value} className="flex items-center gap-1.5 px-2 py-1 rounded text-[11px] cursor-pointer"
                                  style={{
                                    border: `1px solid ${checked ? "var(--vb-accent)" : "rgba(0,0,0,0.12)"}`,
                                    background: checked ? "color-mix(in srgb, var(--vb-accent) 12%, transparent)" : "transparent",
                                  }}
                                  data-testid={`facility-clean-area-${a.value}`}
                                  onClick={(e) => { e.preventDefault(); toggleCleanlinessArea(a.value); }}
                                >
                                  <input type="checkbox" checked={checked} readOnly tabIndex={-1} style={{ pointerEvents: "none" }} />
                                  {a.label}
                                </label>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </label>
                  </div>
                  <input className="vb-input" placeholder="Location (e.g. Level 2, East Wing)" value={form.location || ""} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="facility-location" />
                  <div>
                    <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider opacity-70">Person in charge</label>
                    <select
                      className="vb-input"
                      value={form.person_in_charge || ""}
                      onChange={(e) => setForm({ ...form, person_in_charge: e.target.value })}
                      data-testid="facility-pic"
                    >
                      <option value="">— Unassigned —</option>
                      {picOptions.map((u) => (
                        <option key={u.id} value={u.name}>
                          {u.name}{u.company_role ? ` · ${u.company_role}` : ""}{u.role ? ` (${u.role})` : ""}
                        </option>
                      ))}
                      {form.person_in_charge && !picOptions.some((u) => u.name === form.person_in_charge) && (
                        <option value={form.person_in_charge}>{form.person_in_charge} (legacy / unassigned to site)</option>
                      )}
                    </select>
                    <p className="text-[11px] opacity-60 mt-1">
                      Only shows team members assigned to <strong>{sites.find((s) => s.id === form.site_id)?.name || "this site"}</strong> (plus owners/chain managers).
                    </p>
                  </div>
                </>
              )}
              <div className="flex gap-2 justify-end">
                <button type="button" className="vb-btn ghost text-sm" onClick={() => setModal(null)}>Cancel</button>
                <button type="submit" className="vb-btn text-sm" data-testid={modal === "site" ? "site-submit" : "facility-submit"}>Save</button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
